/**
 * Enquiries · Data Ops · Sep 2026
 * Every /apply web-form submission with its email-notification status, so
 * the founder can see forms that were filled even when the email never arrived.
 * Route: /data-ops/enquiries · GET /api/applications · GET /api/applications/{id}/attachment
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Inbox, Paperclip, Mail, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  sent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  failed: 'bg-rose-100 text-rose-700 border-rose-200',
  unknown: 'bg-slate-100 text-slate-600 border-slate-200',
};

function NotifyBadge({ row }) {
  const s = row.notify_status || 'unknown';
  const label = s === 'sent' ? 'Email sent' : s === 'failed' ? 'Email failed' : 'Email status unknown (pre-Sep 10)';
  return (
    <Badge className={`text-[10px] border ${STATUS[s]}`} title={row.notify_error || (row.notify_to || []).join(', ')} data-testid={`enquiry-notify-${row.id}`}>
      <Mail className="w-3 h-3 mr-1" />{label}
    </Badge>
  );
}

async function downloadAttachment(row) {
  try {
    const res = await axios.get(`${API}/api/applications/${row.id}/attachment`, { responseType: 'blob', withCredentials: true });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = row.file_name || 'attachment'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  } catch (e) {
    toast.error(e?.response?.status === 404 ? 'No attachment stored for this enquiry' : 'Could not download the attachment');
  }
}

function EnquiryRow({ row }) {
  const isPharma = row.audience_type === 'pharma';
  const fields = isPharma
    ? [['Therapeutic area', row.therapeutic_area], ['Preferred stage', row.stage], ['Preferred geography', row.indication]]
    : [['Asset', row.asset_name], ['Therapeutic area', row.therapeutic_area], ['Indication', row.indication], ['Modality', row.modality], ['Stage', row.stage]];
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`enquiry-row-${row.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">{row.name} <span className="text-slate-400 font-normal">· {row.company}</span></p>
          <p className="text-xs text-slate-500"><a href={`mailto:${row.email}`} className="text-sky-700 hover:underline">{row.email}</a>{row.role ? ` · ${row.role}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`text-[10px] ${isPharma ? 'bg-sky-50 text-sky-800 border border-sky-200' : 'bg-lime-50 text-lime-800 border border-lime-200'}`}>{isPharma ? 'Pharma / Large Biotech' : 'Emerging Biotech'}</Badge>
          <NotifyBadge row={row} />
          <span className="text-[11px] text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</span>
        </div>
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 mt-3 text-xs">
        {fields.filter(([, v]) => v).map(([k, v]) => (
          <div key={k}><dt className="text-slate-400">{k}</dt><dd className="text-slate-800">{v}</dd></div>
        ))}
        {row.primary_goal && <div><dt className="text-slate-400">Primary goal</dt><dd className="text-slate-800">{row.primary_goal}</dd></div>}
        {row.program_interest && <div><dt className="text-slate-400">Program interest</dt><dd className="text-slate-800">{row.program_interest}</dd></div>}
      </dl>
      {row.additional_notes && <p className="mt-3 text-xs text-slate-700 bg-slate-50 rounded p-2 whitespace-pre-wrap">{row.additional_notes}</p>}
      {row.notify_error && <p className="mt-2 text-[11px] text-rose-600">Notification error: {row.notify_error}</p>}
      {row.file_name && (
        <button type="button" onClick={() => downloadAttachment(row)} className="inline-flex items-center gap-1 mt-3 text-xs text-sky-700 hover:underline" data-testid={`enquiry-attachment-${row.id}`}>
          <Paperclip className="w-3 h-3" /> {row.file_name}
        </button>
      )}
    </div>
  );
}

export default function EnquiriesPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/api/applications?limit=500`, { withCredentials: true });
      setRows(res.data.applications || []); setRecipients(res.data.notify_recipients || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const needle = q.trim().toLowerCase();
  const shown = rows.filter(r => !needle || `${r.name} ${r.company} ${r.email} ${r.asset_name} ${r.therapeutic_area}`.toLowerCase().includes(needle));
  const failed = rows.filter(r => r.notify_status !== 'sent').length;

  return (
    <div className="p-6 space-y-4" data-testid="enquiries-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Inbox className="w-5 h-5" /> Web-form Enquiries</h1>
          <p className="text-xs text-slate-500 mt-1">Every submission from foresightrx.com/apply — stored here even when the notification email never arrived. Notifications go to {recipients.join(', ') || '…'}.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, company, asset…" className="h-8 pl-7 text-xs w-64" data-testid="enquiries-search" />
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="enquiries-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>
      <div className="flex gap-3 text-xs text-slate-600" data-testid="enquiries-summary">
        <span><b>{rows.length}</b> submissions</span>
        <span><b>{failed}</b> without a confirmed email notification</span>
      </div>
      {error && <p className="text-sm text-rose-600" data-testid="enquiries-error">{error}</p>}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="enquiries-empty">No submissions{needle ? ' match your search' : ' yet'}.</p>
      ) : (
        <div className="space-y-3">{shown.map(r => <EnquiryRow key={r.id} row={r} />)}</div>
      )}
    </div>
  );
}
